<template>
  <span class="clipboard-quick-insert">
    <button
      :id="triggerId"
      ref="trigger"
      type="button"
      :class="['clipboard-quick-insert__trigger', triggerClass, { active: open }]"
      title="Insert from Clipboard"
      :disabled="disabled"
      aria-haspopup="dialog"
      :aria-expanded="open ? 'true' : 'false'"
      @click="onTriggerClick"
    >
      <slot name="icon">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
          class="clipboard-quick-insert__icon"
          aria-hidden="true"
        >
          <rect
            width="8"
            height="4"
            x="8"
            y="2"
            rx="1"
            ry="1"
          />
          <path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2" />
        </svg>
      </slot>
    </button>

    <transition name="clipboard-quick-insert-fade">
      <div
        v-if="open"
        ref="flyout"
        class="clipboard-quick-insert__flyout"
        :style="flyoutStyle"
        role="dialog"
        aria-labelledby="clipboard-quick-insert-title"
        @click.stop
      >
        <div class="clipboard-quick-insert__header">
          <div class="clipboard-quick-insert__header-title">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
              class="clipboard-quick-insert__header-icon"
              aria-hidden="true"
            >
              <rect
                width="8"
                height="4"
                x="8"
                y="2"
                rx="1"
                ry="1"
              />
              <path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2" />
            </svg>
            <span
              id="clipboard-quick-insert-title"
              class="clipboard-quick-insert__header-label"
            >Insert from Clipboard</span>
          </div>
          <button
            type="button"
            class="clipboard-quick-insert__open-all"
            @click="onOpenAll"
          >
            Open all
          </button>
        </div>
        <div class="clipboard-quick-insert__list">
          <div
            v-if="loading"
            class="clipboard-quick-insert__status"
          >
            Loading…
          </div>
          <div
            v-else-if="loadError"
            class="clipboard-quick-insert__status clipboard-quick-insert__status--error"
          >
            {{ loadError }}
          </div>
          <div
            v-else-if="displayItems.length === 0"
            class="clipboard-quick-insert__status"
          >
            Nothing saved yet.
          </div>
          <button
            v-for="item in displayItems"
            :key="item.id"
            type="button"
            class="clipboard-quick-insert__item"
            @click="onSelectItem(item)"
          >
            <div class="clipboard-quick-insert__item-head">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="12"
                height="12"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
                class="clipboard-quick-insert__item-sparkles"
                aria-hidden="true"
              >
                <path d="M9.937 15.5A2 2 0 0 0 8.5 14.063l-6.135-1.582a.5.5 0 0 1 0-.962L8.5 9.936A2 2 0 0 0 9.937 8.5l1.582-6.135a.5.5 0 0 1 .963 0L14.063 8.5A2 2 0 0 0 15.5 9.937l6.135 1.581a.5.5 0 0 1 0 .964L15.5 14.063a2 2 0 0 0-1.437 1.437l-1.582 6.135a.5.5 0 0 1-.963 0z" />
                <path d="M20 3v4" />
                <path d="M22 5h-4" />
                <path d="M4 17v2" />
                <path d="M5 18H3" />
              </svg>
              <span>{{ typeLabel(item.type) }}</span>
              <template v-if="itemHasTitle(item)">
                <span aria-hidden="true">·</span>
                <span class="clipboard-quick-insert__item-title">{{ item.title }}</span>
              </template>
            </div>
            <p class="clipboard-quick-insert__item-preview">
              {{ previewContent(item) }}
            </p>
          </button>
        </div>
      </div>
    </transition>
  </span>
</template>

<script>
import {
  CLIPBOARD_SORT_NEWEST,
  CLIPBOARD_TYPE_META,
} from './clipboard-constants';
import { getDisplayContent, hasMeaningfulTitle, sortClipboardItems } from './clipboard-formatters';
import { fetchClipboardItems } from '../../../services/api/clipboard';

const FLYOUT_WIDTH = 320;
const VIEWPORT_GAP = 8;

export default {
  name: 'ClipboardQuickInsertPopover',

  inject: {
    layoutClipboardActions: {
      default: null,
    },
  },

  props: {
    disabled: {
      type: Boolean,
      default: false,
    },
    triggerClass: {
      type: [String, Array, Object],
      default: '',
    },
  },

  data() {
    return {
      triggerId: `clipboard-quick-insert-${this._uid}`,
      open: false,
      items: [],
      loading: false,
      loadError: null,
      flyoutStyle: {
        top: '0px',
        left: '0px',
      },
    };
  },

  computed: {
    displayItems() {
      return sortClipboardItems(this.items, CLIPBOARD_SORT_NEWEST);
    },
  },

  watch: {
    open(isOpen) {
      if (isOpen) {
        this.bindGlobalListeners();
        this.$nextTick(() => {
          this.attachFlyoutToBody();
          this.positionFlyout();
          requestAnimationFrame(() => this.positionFlyout());
        });
      } else {
        this.unbindGlobalListeners();
        this.detachFlyoutFromBody();
      }
    },
  },

  beforeDestroy() {
    this.unbindGlobalListeners();
    this.detachFlyoutFromBody();
  },

  methods: {
    typeLabel(type) {
      return CLIPBOARD_TYPE_META[type]?.label || type || '';
    },

    previewContent(item) {
      return getDisplayContent(item);
    },

    // Hide the label when it's just an auto-derived copy of the content. (POR-13563-2)
    itemHasTitle(item) {
      return hasMeaningfulTitle(item);
    },

    onTriggerClick() {
      if (this.disabled) {
        return;
      }
      if (this.open) {
        this.close();
        return;
      }
      this.open = true;
      this.loadItems();
    },

    close() {
      this.open = false;
    },

    attachFlyoutToBody() {
      const el = this.$refs.flyout;
      if (!el || el.parentNode === document.body) {
        return;
      }
      this._flyoutHome = {
        parent: el.parentNode,
        next: el.nextSibling,
      };
      document.body.appendChild(el);
    },

    detachFlyoutFromBody() {
      const el = this.$refs.flyout;
      const home = this._flyoutHome;
      if (!el || !home?.parent || el.parentNode !== document.body) {
        return;
      }
      home.parent.insertBefore(el, home.next);
      this._flyoutHome = null;
    },

    bindGlobalListeners() {
      if (this._listenersBound) {
        return;
      }
      this._listenersBound = true;
      document.addEventListener('click', this.onDocumentClick, true);
      document.addEventListener('keydown', this.onDocumentKeydown);
      window.addEventListener('resize', this.positionFlyout);
      window.addEventListener('scroll', this.positionFlyout, true);
    },

    unbindGlobalListeners() {
      if (!this._listenersBound) {
        return;
      }
      this._listenersBound = false;
      document.removeEventListener('click', this.onDocumentClick, true);
      document.removeEventListener('keydown', this.onDocumentKeydown);
      window.removeEventListener('resize', this.positionFlyout);
      window.removeEventListener('scroll', this.positionFlyout, true);
    },

    onDocumentClick(event) {
      const trigger = this.$refs.trigger;
      const flyout = this.$refs.flyout;
      if (trigger?.contains(event.target) || flyout?.contains(event.target)) {
        return;
      }
      this.close();
    },

    onDocumentKeydown(event) {
      if (event.key === 'Escape') {
        this.close();
      }
    },

    /**
     * Opens to the right of the trigger (toward the chat / sidebar).
     * Falls back to above (Lovable top+start) when there is not enough room on the right.
     */
    positionFlyout() {
      const trigger = this.$refs.trigger;
      const flyout = this.$refs.flyout;
      if (!trigger || !flyout || !this.open) {
        return;
      }

      const rect = trigger.getBoundingClientRect();
      const flyoutHeight = flyout.offsetHeight;
      const flyoutWidth = FLYOUT_WIDTH;
      const viewportW = window.innerWidth;
      const viewportH = window.innerHeight;

      let top;
      let left;

      const fitsRight = rect.right + VIEWPORT_GAP + flyoutWidth <= viewportW - VIEWPORT_GAP;

      if (fitsRight) {
        left = rect.right + VIEWPORT_GAP;
        top = rect.top + (rect.height / 2) - (flyoutHeight / 2);
      } else {
        left = rect.left;
        top = rect.top - flyoutHeight - VIEWPORT_GAP;
        if (top < VIEWPORT_GAP) {
          top = rect.bottom + VIEWPORT_GAP;
        }
      }

      if (top + flyoutHeight > viewportH - VIEWPORT_GAP) {
        top = viewportH - flyoutHeight - VIEWPORT_GAP;
      }
      if (top < VIEWPORT_GAP) {
        top = VIEWPORT_GAP;
      }

      if (left + flyoutWidth > viewportW - VIEWPORT_GAP) {
        left = viewportW - flyoutWidth - VIEWPORT_GAP;
      }
      if (left < VIEWPORT_GAP) {
        left = VIEWPORT_GAP;
      }

      this.flyoutStyle = {
        position: 'fixed',
        top: `${Math.round(top)}px`,
        left: `${Math.round(left)}px`,
        width: `${flyoutWidth}px`,
        zIndex: '10060',
      };
    },

    async loadItems() {
      this.loading = true;
      this.loadError = null;
      try {
        this.items = await fetchClipboardItems();
      } catch (err) {
        console.error('[Clipboard] quick insert load failed', err);
        this.loadError = 'Could not load items.';
        this.items = [];
      } finally {
        this.loading = false;
        this.$nextTick(() => this.positionFlyout());
      }
    },

    onSelectItem(item) {
      this.$emit('select', item);
      this.close();
    },

    onOpenAll() {
      if (this.layoutClipboardActions?.openFullDrawer) {
        this.layoutClipboardActions.openFullDrawer();
      } else {
        this.$emit('open-all');
      }
      this.close();
    },
  },
};
</script>

<style scoped>
.clipboard-quick-insert {
  display: inline-flex;
  align-items: center;
}

.clipboard-quick-insert__trigger {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  border: none;
  background: transparent;
  cursor: pointer;
  color: inherit;
}

.clipboard-quick-insert__trigger:disabled {
  cursor: not-allowed;
  opacity: 0.5;
}

.clipboard-quick-insert__icon {
  flex-shrink: 0;
}

/* Lovable: z-50 rounded-md border bg-popover shadow-md w-80 p-0 */
.clipboard-quick-insert__flyout {
  box-sizing: border-box;
  width: 20rem;
  max-width: 20rem;
  padding: 0;
  border: 1px solid hsl(214.3, 31.8%, 91.4%);
  border-radius: 0.375rem;
  background-color: hsl(0, 0%, 100%);
  color: hsl(222.2, 84%, 4.9%);
  box-shadow:
    0 4px 6px -1px rgba(0, 0, 0, 0.1),
    0 2px 4px -2px rgba(0, 0, 0, 0.1);
  outline: none;
  font-family: ui-sans-serif, system-ui, -apple-system, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
  font-size: 0.875rem;
  line-height: 1.25rem;
}

.clipboard-quick-insert-fade-enter-active,
.clipboard-quick-insert-fade-leave-active {
  transition: opacity 150ms ease, transform 150ms ease;
  transform-origin: left center;
}

.clipboard-quick-insert-fade-enter,
.clipboard-quick-insert-fade-leave-to {
  opacity: 0;
  transform: scale(0.95);
}

/* flex items-center justify-between px-3 py-2 border-b border-border */
.clipboard-quick-insert__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.375rem;
  padding: 0.5rem 0.75rem;
  border-bottom: 1px solid hsl(214.3, 31.8%, 91.4%);
}

/* flex items-center gap-1.5 */
.clipboard-quick-insert__header-title {
  display: flex;
  align-items: center;
  gap: 0.375rem;
  min-width: 0;
}

/* h-3.5 w-3.5 text-muted-foreground */
.clipboard-quick-insert__header-icon {
  flex-shrink: 0;
  width: 0.875rem;
  height: 0.875rem;
  color: hsl(215.4, 16.3%, 46.9%);
}

/* text-xs font-semibold */
.clipboard-quick-insert__header-label {
  font-size: 0.75rem;
  font-weight: 600;
  line-height: 1rem;
  color: hsl(222.2, 84%, 4.9%);
}

/* text-xs text-primary hover:underline */
.clipboard-quick-insert__open-all {
  flex-shrink: 0;
  padding: 0;
  border: none;
  background: transparent;
  font-size: 0.75rem;
  font-weight: 400;
  line-height: 1rem;
  color: #1abc9c;
  cursor: pointer;
  white-space: nowrap;
}

.clipboard-quick-insert__open-all:hover {
  color: #15967d;
  text-decoration: underline;
}

/* Scrollable list — viewport fits ~5 items (Lovable max-h-72) */
.clipboard-quick-insert__list {
  max-height: 18rem;
  overflow-x: hidden;
  overflow-y: auto;
  padding: 0.25rem 0;
  -webkit-overflow-scrolling: touch;
}

.clipboard-quick-insert__status {
  padding: 0.75rem;
  font-size: 0.75rem;
  line-height: 1rem;
  color: hsl(215.4, 16.3%, 46.9%);
  text-align: center;
}

.clipboard-quick-insert__status--error {
  color: hsl(0, 84.2%, 60.2%);
}

/* w-full text-left px-3 py-2 hover:bg-muted/60 border-b border-border/50 */
.clipboard-quick-insert__item {
  display: block;
  width: 100%;
  padding: 0.5rem 0.75rem;
  border: none;
  border-bottom: 1px solid hsla(214.3, 31.8%, 91.4%, 0.5);
  background: transparent;
  text-align: left;
  cursor: pointer;
  transition: background-color 120ms ease;
}

.clipboard-quick-insert__item:last-child {
  border-bottom: none;
}

.clipboard-quick-insert__item:hover {
  background-color: hsla(210, 40%, 96.1%, 0.6);
}

/* text-[10px] uppercase tracking-wide text-muted-foreground gap-1.5 */
.clipboard-quick-insert__item-head {
  display: flex;
  align-items: center;
  gap: 0.375rem;
  font-size: 10px;
  line-height: 1.2;
  text-transform: uppercase;
  letter-spacing: 0.025em;
  color: hsl(215.4, 16.3%, 46.9%);
}

.clipboard-quick-insert__item-sparkles {
  flex-shrink: 0;
  width: 0.75rem;
  height: 0.75rem;
}

/* font-semibold text-foreground normal-case tracking-normal */
.clipboard-quick-insert__item-title {
  font-size: 10px;
  font-weight: 600;
  text-transform: none;
  letter-spacing: normal;
  color: hsl(222.2, 84%, 4.9%);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  min-width: 0;
}

/* mt-1 text-xs text-foreground/80 leading-snug line-clamp-2 */
.clipboard-quick-insert__item-preview {
  margin: 0.25rem 0 0;
  padding: 0;
  font-size: 0.75rem;
  font-weight: 400;
  line-height: 1.375;
  color: hsla(222.2, 84%, 4.9%, 0.8);
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
</style>
